import moment from 'moment';
import ClaimApi from './ClaimApi';

/**
 * The claim queue.
 *
 * The status filter is built from `/config`, not from a list written here: three
 * front-ends render these states and a fourth copy of the vocabulary is how the
 * same claim comes to read as different things in different apps.
 *
 * The GROUPING is this screen's own, and it is by what an officer can act on
 * rather than by the raw state — fifteen chips is not a filter, it is noise.
 */
export default {
    mixins: [ClaimApi],

    data: () => ({
        claims: [],
        pagination: {},
        isLoading: true,

        // Filters
        search: '',
        selectedGroup: 'incoming',
        selectedStatus: '',
        selectedKind: '',
        selectedParty: '',
        selectedBranch: '',
        branches: [],
        fromDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        toDate: moment().format('YYYY-MM-DD'),

        per_page: 20,

        /**
         * The queue's tabs, in the office's own words (owner, 2026-10-16). Five stages of a case,
         * each with the one-line meaning the office gave it, shown under the tabs so a new officer
         * does not have to guess which pile a claim is in.
         *
         * ຍົກເລີກ is in none of them — only under ທັງໝົດ. A cancelled case was never paid, and
         * filing it under ສຳເລັດການຊົດເຊີຍ would tell whoever reads that tab the customer got money.
         *
         * `incoming` is the default: a claim nobody has picked up is the work that waits on us.
         */
        groups: [
            {
                id: 'incoming',
                name: 'ຄຳຮ້ອງຈາກລູກຄ້າ',
                hint: 'ລູກຄ້າຍື່ນຄຳຮ້ອງມາແລ້ວ ແຕ່ຍັງບໍ່ກົດຮັບເລື່ອງ',
                statuses: ['pending'],
            },
            {
                id: 'mine',
                name: 'ກຳລັງດຳເນີນການ',
                hint: 'ທີມງານຮັບເລື່ອງ / ຕິດຕໍ່ຫາລູກຄ້າ ແລະ ປະເມີນ',
                statuses: ['accepted', 'contacted', 'assessed', 'returned_for_fix'],
            },
            {
                id: 'waiting',
                name: 'ອະນຸມັດການຊົດເຊີຍ',
                hint: 'ສົ່ງເຂົ້າ ERP ແລ້ວ',
                statuses: ['submitted', 'in_approval', 'waiting_budget', 'approved'],
            },
            {
                id: 'rejected',
                name: 'ປະຕິເສດການຊົດເຊີຍ',
                hint: 'ບິນທີ່ຂໍ້ມູນບໍ່ຄົບຖ້ວນ ຫຼື ຊົດເຊີຍບໍ່ໄດ້',
                statuses: ['rejected'],
            },
            {
                id: 'closed',
                name: 'ສຳເລັດການຊົດເຊີຍ',
                hint: 'ໂອນເງິນໃຫ້ລູກຄ້າແລ້ວ',
                statuses: ['resolved'],
            },
            { id: '', name: 'ທັງໝົດ', hint: '', statuses: [] },
        ],

        /** The screen a stale link tried to reach, when the router sent them back here. */
        blockedDesk: '',
    }),

    created() {
        // Why they are here instead of where they clicked. Read from the QUERY, not from route
        // meta: `/claims` is one route and one component, so a navigation between two of its URLs
        // reuses this instance and never runs `created` again — which is exactly how the two desk
        // queues came to render each other's columns in production.
        this.blockedDesk = this.$route.query.blocked || '';
    },

    computed: {
        /**
         * Why they are looking at this queue instead of the screen they asked for.
         *
         * A route refused for authority sends them here rather than to a page-not-found: the screen
         * exists, it is the other desk's work, and saying so is the same promise the workbench makes
         * about its buttons.
         */
        blockedNotice() {
            if (this.blockedDesk === 'assess') return 'ໜ້າປະເມີນເປັນວຽກຂອງຝ່າຍປະເມີນ — ທ່ານບໍ່ມີສິດເຂົ້າ';
            if (this.blockedDesk === 'file') return 'ໜ້າເປີດຄຳຮ້ອງເປັນວຽກຂອງຝ່າຍຮັບຄຳຮ້ອງ — ທ່ານບໍ່ມີສິດເຂົ້າ';

            return '';
        },

        /** May this user open a claim on a customer's behalf? Same authority as the route's. */
        mayFile() {
            return this.$can(['SuperAdmin', 'injured-parcel-user', 'file-claim']);
        },

        /** `[{value,label}]` straight from the API — never a map written here. */
        statusOptions() {
            return this.$store.getters['Claim/options']('statuses')
                || (this.$store.getters['Claim/config'] || {}).statuses?.staff
                || [];
        },
        kindOptions() {
            return this.$store.getters['Claim/options']('claim_kind');
        },
        /**
         * The four kinds a claim can CARRY, not the three the assessor is asked. The queue filters
         * on what is stored — a claim says `sorting_centre`, and nobody ever filed one as "a unit".
         */
        partyOptions() {
            return this.$store.getters['Claim/options']('liable_party_kind');
        },
        /** What the chosen tab means, in the office's words. */
        selectedGroupHint() {
            const group = this.groups.find((g) => g.id === this.selectedGroup);
            return group ? group.hint : '';
        },
        /** The statuses the chosen group covers, sent as a filter. */
        groupStatuses() {
            const group = this.groups.find((g) => g.id === this.selectedGroup);
            return group ? group.statuses : [];
        },
    },

    methods: {
        /**
         * What the claimant is to the bill, for the queue column.
         *
         * Read from the published vocabulary, never worked out here — the row does not even carry
         * the bill's parties to work it out FROM, which is the point: the server decided it at
         * intake, from the check that admitted the claim.
         */
        claimantRoleLabel(value) {
            return this.$store.getters['Claim/claimantRoleLabel'](value);
        },

        /**
         * Which party carries the cost — the assessment desk's own outcome column.
         *
         * It was only ever a FILTER, which meant an assessor could narrow by it and still not see
         * it on the rows that came back. From the published vocabulary like every other word here.
         */
        partyKindLabel(value) {
            const found = this.partyOptions.find((p) => p.value === value);
            return found ? found.label : '';
        },

        statusLabel(value) {
            return this.$store.getters['Claim/statusLabel'](value);
        },

        /**
         * What a claim of several parcels came to, when they disagree.
         *
         * The status alone cannot say it: a claim with one box paid and another refused is neither
         * "paid" nor "refused". Shown only on multi-parcel rows, where it is the difference between
         * a queue that reads correctly and one that reads a case by half of it.
         */
        outcomeLabel(value) {
            return this.$store.getters['Claim/outcomeLabel'](value);
        },

        fetchClaims(page = 1) {
            this.isLoading = true;

            return this.claimList({
                search: this.search || undefined,
                status: this.selectedStatus || undefined,
                // A group is several statuses; the API filters one at a time, so
                // an explicit status wins and the group is applied client-side
                // over the page. Kept deliberately simple until a queue is big
                // enough to justify a server-side group filter.
                claim_kind: this.selectedKind || undefined,
                liable_party_kind: this.selectedParty || undefined,
                // Real filters again as of `fix-claim-reads-blocking-frontends`.
                // These three were sent and silently DROPPED by the API for a
                // while: narrowing the queue returned the same list and looked
                // like a broken screen, so they were removed rather than faked
                // client-side. The backend now filters `responsible_branch_id`
                // and a window over `created_at` — the FILING date, so the set
                // does not shift on its own every time the ERP poller runs.
                branch_id: this.selectedBranch || undefined,
                from: this.fromDate || undefined,
                to: this.toDate || undefined,
                page,
                per_page: this.per_page,
            })
                .then((res) => {
                    const body = res.data.data;
                    this.claims = this.applyGroup(body.data || []);
                    this.pagination = body;
                })
                .catch((error) => this.handleActionError(error))
                .finally(() => {
                    this.isLoading = false;
                });
        },

        applyGroup(rows) {
            const statuses = this.groupStatuses;

            if (!statuses.length) {
                return rows;
            }

            return rows.filter((row) => statuses.indexOf(row.status) !== -1);
        },

        /**
         * `pending` means two opposite things and the status alone cannot tell
         * them apart — the contract publishes `action_required_when` for exactly
         * this. A claim waiting on the CLAIMANT is not a claim waiting on us.
         */
        isWaitingOnClaimant(row) {
            return row.status === 'pending' && !!row.info_request;
        },

        fetchBranches() {
            return this.claimBranches()
                .then((res) => {
                    this.branches = res.data.data || [];
                })
                .catch(() => {
                    this.branches = [];
                });
        },

        resetFilters() {
            this.search = '';
            this.selectedStatus = '';
            this.selectedKind = '';
            this.selectedParty = '';
            this.selectedBranch = '';
            this.fromDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
            this.toDate = moment().format('YYYY-MM-DD');
            this.fetchClaims(1);
        },
    },
};
